import { patchSessionClinicalNote, PatchSessionInput, SessionFormField, SessionResource, submitSession, submitSessionForStudent } from '@uplift/api';
import { providerSubmitSessionRoute, ROUTING_CONFIG } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { SubSystem, YesNo } from '@uplift/types';
import { TIME_RANGE_DATE_VALUE_FORMAT, useScrollableView, useAlert } from '@uplift/ui-v2';
import { FormikErrors, FormikHelpers, yupToFormErrors } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useClinicalNotesContext } from '../clinical-notes.context';
import { getDiagnosisInitialValue, getDynamicFormsInitialValues, getInputValuesFromDynamicFormsValues, getOptedValueFromDynamicFormYesNo, getSessionFormsFromSessionNotes, getStartAndEndTimeInput, getTreatmentInitialValues, getYesNoForDynamicFormFromOptedValue, participantOptions, serviceOptions } from '../helpers';
import { ConfirmRecommendationEnum, confirmRecommendations } from '../sections';
import { ClinicalNotesDynamicFormFieldHash, ClinicalNotesFormType } from '../types';
import { getClinicalNotesDynamicSectionsErrorsObject, getClinicalNotesValidationSchema, validateClinicalNotesDynamicSections } from '../validation';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionClinicalNoteQuery } from '@uplift/queries';
import snakecaseKeys from 'snakecase-keys';


export const useClinicalNotesForm = () => {
  const l10n = useL10n();
  const alert = useAlert();
  const timezone = useTimezone();
  const queryClient = useQueryClient();
  const { scrollTo } = useScrollableView();
  const { state: { sessionResource: _sessionResource, previousSessionResource, appointment, diagnoses } } = useClinicalNotesContext();
  const sessionId = _sessionResource.session.id;
  const appointmentId = _sessionResource.session.appointmentId;
  const { data: clinicalNotes } = useSessionClinicalNoteQuery(sessionId, { suspense: true });
  const [ dynamicSectionsErrorObj, setDynamicSectionsErrorObj ] = useState(() => {
    return getClinicalNotesDynamicSectionsErrorsObject(clinicalNotes);
  });
  const history = useHistory();
  const redirectTo = providerSubmitSessionRoute.useQueryParam('redirect_to');

  const onReroute = useCallback(() => {
    if (redirectTo) {
      return history.push(redirectTo);
    }
    return history.push(ROUTING_CONFIG.main);
  }, [ history, redirectTo ]);

  const fieldHash = useMemo(() => getFieldHash(clinicalNotes.notes), [ clinicalNotes.notes ]);

  const { dynamicForm, dynamicYesNoKeyHash } = useMemo(() => getDynamicFormsInitialValues(clinicalNotes.notes), [ clinicalNotes.notes ]);
  const initialValues: ClinicalNotesFormType = useMemo(() => {
    const { session, notes } = clinicalNotes;
    const previousSession = previousSessionResource?.session;
    const dynamicYesNoField: Partial<ClinicalNotesFormType> = {};
    const mentalStatusExamWithinNormalRangeYesNo = getYesNoForDynamicFormFromOptedValue(notes.mentalStatusExamOpted, 'mentalStatusExam');

    if (mentalStatusExamWithinNormalRangeYesNo) {
      dynamicYesNoField.mentalStatusExamWithinNormalRangeYesNo = mentalStatusExamWithinNormalRangeYesNo;
    }

    const riskAssessmentDenyYesNo = getYesNoForDynamicFormFromOptedValue(notes.riskAssessmentOpted, 'riskAssessment');

    if (riskAssessmentDenyYesNo) {
      dynamicYesNoField.riskAssessmentDenyYesNo = riskAssessmentDenyYesNo;
    }

    if (notes.treatment?.homework) {
      dynamicYesNoField.treatmentHomeworkYesNo = YesNo.Yes;
    }

    const treatmentInitialValues = getTreatmentInitialValues(notes.treatment.approachSelections);
    const diagnosisInitial = getDiagnosisInitialValue(session, previousSession, diagnoses);
    const participantInitialValue = session?.participation ?? previousSession?.participation ?? participantOptions[0].value;
    const serviceInitialValue = session?.serviceType ?? previousSession?.serviceType ?? serviceOptions[0].value;

    // TODO: handle treatment and diagnosis prefill, add checked checkbox

    return {
      ...dynamicYesNoField,

      assessments: notes.assessments,
      template: notes.isInitialSession ? 'initial' : 'followUp',
      participant: participantInitialValue,
      service: serviceInitialValue,
      diagnosis: diagnosisInitial,
      isDiagnosisReviewed: notes.isDiagnosisReviewed ?? false,
      isTreatmentReviewed: notes.treatment.isTreatmentReviewed ?? false,
      isFollowUpTreatmentGoalReviewed: notes.isFollowUpTreatmentGoalReviewed ?? false,
      treatment: treatmentInitialValues,
      treatmentHomework: notes.treatment?.homework ?? '',
      primaryCptCode: session?.cptCodeId ?? '',
      addonCptCodes: session?.addonCptCodeIds || [],
      additionalDetails: notes?.additionalDetails ?? '',
      recommendationConfirmation: false,
      sessionDuration: {
        start: CalendarDateService.date(notes?.startTime ?? appointment.fromDatetime).tz(timezone).format(TIME_RANGE_DATE_VALUE_FORMAT),
        end: CalendarDateService.date(notes?.endTime ?? appointment.toDatetime).tz(timezone).subtract(notes?.endTime ? 0 : 5, 'minutes').format(TIME_RANGE_DATE_VALUE_FORMAT)
      },
      identityVerified: null,
      ...dynamicForm,
    };
  }, [ clinicalNotes, previousSessionResource?.session, diagnoses, appointment.fromDatetime, appointment.toDatetime, timezone, dynamicForm ]);
  const [ valuesToTestAgainst, setValuesToTestAgainst ] = useState(initialValues);

  const showError = useCallback((message?: string) => {
    scrollTo({ top: 0, behavior: 'smooth' });
    alert.error(message);
  }, [ alert, scrollTo ]);

  const getPatchInput = useCallback((values: ClinicalNotesFormType): PatchSessionInput => {
    const isInitial = values.template === 'initial';
    const dynamicInputValues = getInputValuesFromDynamicFormsValues(values, clinicalNotes.notes);

    const treatment: PatchSessionInput['treatment'] = {
      isTreatmentReviewed: values.isTreatmentReviewed,
      approachSelections: values.treatment.filter(({ treatmentApproachId, interventionIds }) => treatmentApproachId && !!interventionIds.length),
    };

    if (values.treatmentHomework ) {
      treatment['homework'] = values.treatmentHomeworkYesNo === YesNo.Yes ? values.treatmentHomework : '';
    }

    return {
      ...dynamicInputValues,
      assessments: values.assessments.map(v => snakecaseKeys(v)),
      isInitialSession: isInitial,
      riskAssessmentOpted: getOptedValueFromDynamicFormYesNo(values.riskAssessmentDenyYesNo, 'riskAssessment'),
      mentalStatusExamOpted: getOptedValueFromDynamicFormYesNo(values.mentalStatusExamWithinNormalRangeYesNo, 'mentalStatusExam'),
      treatment: treatment,
      serviceType: values.service,
      participation: values.participant,
      presentingProblems: values.presentingProblems,
      additionalDetails: values.additionalDetails?.trim() || undefined,
      diagnosis_ids: values.diagnosis.length ? values.diagnosis : undefined,
      isDiagnosisReviewed: values.isDiagnosisReviewed,
      isFollowUpTreatmentGoalReviewed: values.isFollowUpTreatmentGoalReviewed,
      ...getStartAndEndTimeInput(appointment.fromDatetime, timezone, values.sessionDuration.start, values.sessionDuration.end)
    };
  }, [ appointment.fromDatetime, clinicalNotes.notes, timezone ]);

  const onSubmit = useCallback(async (values: ClinicalNotesFormType, helpers: FormikHelpers<ClinicalNotesFormType>) => {
    const _patchInput = getPatchInput(values);
    const input: PatchSessionInput = {
      ..._patchInput,
      diagnosis_ids: values.diagnosis,
    };

    try {
      const { data: newSessionResource } = await patchSessionClinicalNote(sessionId, input);

      const dynamicSectionsErrorObj = getClinicalNotesDynamicSectionsErrorsObject(newSessionResource);

      const formikErrors = validateClinicalNotesDynamicSections(
        dynamicSectionsErrorObj.formikErrors,
        values,
        values,
        fieldHash
      );

      const dyanmicFormsValid = Object.keys(formikErrors).length === 0;

      if (dyanmicFormsValid) {
        await patchSessionClinicalNote(sessionId, input);
        if (appointment.patient.subsystem === SubSystem.higherEd) {
          await submitSessionForStudent(sessionId, { identityVerified: values.identityVerified ?? null });
        } else {
          await submitSession(sessionId, { identityVerified: values.identityVerified ?? null });
        }
        Promise.all([
          queryClient.invalidateQueries([ 'session', sessionId ]),
          queryClient.invalidateQueries([ 'appointment', appointmentId, 'session' ])
        ]);
        onReroute();
      } else {
        setDynamicSectionsErrorObj(dynamicSectionsErrorObj);
        setValuesToTestAgainst(values);

        helpers.setSubmitting(false);
        helpers.setErrors(formikErrors);
      }
    } catch (error) {
      if (error?.response?.data?.error) {
        showError(error?.response?.data?.error);
      } else if (error?.message) {
        showError(l10n.clinicalNotes.thereWasAnError(error?.message));
      } else {
        showError(l10n.clinicalNotes.thereWasAnError());
      }

      console.error(error);
    }

  }, [ fieldHash, getPatchInput, onReroute, sessionId, showError ]);


  const onPatch = useCallback(async (values: ClinicalNotesFormType, { rerouteOnSuccess = true }: { rerouteOnSuccess?: boolean }) => {
    const input: PatchSessionInput = getPatchInput(values);

    try {
      await patchSessionClinicalNote(sessionId, input);
      await Promise.all([
        queryClient.invalidateQueries([ 'session', sessionId ]),
        queryClient.invalidateQueries([ 'appointment', appointmentId, 'session' ])
      ]);
      rerouteOnSuccess && onReroute();
    } catch (error) {
      showError(l10n.clinicalNotes.thereWasAnError());
      console.error(error);
    }

  }, [ getPatchInput, onReroute, sessionId, appointmentId, showError ]);

  const validationSchema = useMemo(() => {
    return getClinicalNotesValidationSchema(
      dynamicYesNoKeyHash,
      l10n,
      diagnoses,
      clinicalNotes.notes.isDiagnosisReviewRequired,
      clinicalNotes.notes.treatment.isTreatmentReviewRequired,
      clinicalNotes.notes.isFollowUpTreatmentGoalReviewRequired,
    );
  }, [ dynamicYesNoKeyHash, l10n, diagnoses, clinicalNotes.notes.isDiagnosisReviewRequired, clinicalNotes.notes.treatment.isTreatmentReviewRequired, clinicalNotes.notes.isFollowUpTreatmentGoalReviewRequired ]);

  const validateForm = useCallback((values: ClinicalNotesFormType) => {
    let errors: FormikErrors<ClinicalNotesFormType> = {};

    // custom stuff for the followUpProgress.recommendations field
    const recommendation = values.followUpProgress.recommendations as ConfirmRecommendationEnum;
    const requireConfirmation = confirmRecommendations.includes(recommendation);

    try {
      validationSchema.validateSync(values, {
        abortEarly: false,
        context: { requireConfirmation: requireConfirmation ? 'y' : 'n' } // context seems to only allow a single char
      });
    } catch (error) {
      errors = yupToFormErrors<ClinicalNotesFormType>(error);
    }

    return {
      ...validateClinicalNotesDynamicSections(
        dynamicSectionsErrorObj.formikErrors,
        values,
        valuesToTestAgainst,
        fieldHash,
      ),
      ...errors
    };
  }, [ dynamicSectionsErrorObj.formikErrors, valuesToTestAgainst, fieldHash, validationSchema ]);

  return { initialValues, onSubmit, onPatch, validateForm };
};

const getFieldHash = (notes: SessionResource['notes']) => {
  const { sessionForms, dynamicFormsKeys } = getSessionFormsFromSessionNotes(notes);

  return dynamicFormsKeys.reduce((r, dynamicFormKey) => {
    const form = sessionForms[dynamicFormKey];

    const dynamicForm = form.formFields.reduce((r, formField: SessionFormField) => {
      return { ...r, [formField.id]: formField };
    }, {} as ClinicalNotesDynamicFormFieldHash['followUpProgress']);

    return { ...r, [dynamicFormKey]: dynamicForm };
  }, {} as ClinicalNotesDynamicFormFieldHash);
};
